import React, { useState, useLayoutEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Link, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { Visibility, VisibilityOff, PersonOutline, LockOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@/shared/ui';
import { useAuth } from '@/shared/context/AuthContext';
import { useOrgHomePath } from '@/shared/layout/useOrgHomePath';
import AuthPageShell, { authFieldSx } from '@/modules/identity/components/AuthPageShell';

interface LoginFormValues {
  username: string;
  password: string;
  code: string;
  rememberDevice: boolean;
}

const Login: React.FC = () => {
  const { t } = useTranslation('identity');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const { login, completeTwoFactorLogin, isAuthenticated, loading, error } = useAuth();
  const navigate = useNavigate();

  const home = useOrgHomePath();
  const needsTwoFactor = Boolean(tempToken);

  const formSchema = Yup.object().shape(
    needsTwoFactor
      ? {
          code: Yup.string().required(t('login.twoFactorCode')),
          rememberDevice: Yup.boolean(),
        }
      : {
          username: Yup.string().required(t('validation.usernameRequired')),
          password: Yup.string()
            .required(t('validation.passwordRequired'))
            .min(6, t('validation.passwordMin6')),
        }
  );

  // Before paint, so the success snackbar (deferred to the next task) does not
  // sit on the login form while `/auth/me` or this redirect is still in flight.
  useLayoutEffect(() => {
    if (isAuthenticated && home.ready) {
      navigate(home.path);
    }
  }, [home.path, home.ready, isAuthenticated, navigate]);

  const handleSubmit = async (
    values: LoginFormValues,
    { setSubmitting, setFieldValue }: FormikHelpers<LoginFormValues>
  ) => {
    try {
      if (tempToken) {
        await completeTwoFactorLogin(tempToken, values.code.trim(), {
          rememberDevice: values.rememberDevice,
          username: values.username,
        });
        return;
      }

      const result = await login(values.username, values.password);
      if (result?.data?.requires2FA && result?.data?.tempToken) {
        setTempToken(result.data.tempToken);
        setFieldValue('code', '');
      }
    } catch {
      // Error is displayed via AuthContext `error` state
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleBackToLogin = () => {
    setTempToken(null);
  };

  const formCard = (
    <Box sx={{ width: '100%' }}>
        <Typography component="h1" variant="h3" fontWeight={600} align="center" sx={{ mb: 4 }}>
          {tempToken ? t('login.twoFactorTitle') : t('login.title')}
        </Typography>
        {tempToken ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {t('login.twoFactorSubtitle')}
          </Typography>
        ) : null}

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Formik
          initialValues={{
            username: '',
            password: '',
            code: '',
            rememberDevice: true,
          }}
          validationSchema={formSchema}
          onSubmit={handleSubmit}
          validateOnBlur={false}
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <Form autoComplete="on">
              <Stack spacing={2.5}>
                {/* Keep credentials mounted during 2FA so browsers autofill those fields, not OTP */}
                <Box sx={needsTwoFactor ? { display: 'none' } : undefined}>
                  <Field
                    as={TextField}
                    name="username"
                    label={t('login.username')}
                    placeholder={t('login.usernamePlaceholder')}
                    variant="outlined"
                    fullWidth
                    autoComplete="username"
                    autoFocus={!needsTwoFactor}
                    error={touched.username && Boolean(errors.username)}
                    helperText={touched.username && errors.username}
                    sx={authFieldSx}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutline fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Box sx={{ mt: needsTwoFactor ? 0 : 2.5 }}>
                    <Field
                      as={TextField}
                      name="password"
                      label={t('login.password')}
                      placeholder={t('login.passwordPlaceholder')}
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      autoComplete="current-password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      sx={authFieldSx}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: needsTwoFactor ? undefined : (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={t('login.togglePassword')}
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Box>

                {needsTwoFactor && (
                  <>
                    <TextField
                      name="code"
                      label={t('login.twoFactorCode')}
                      variant="outlined"
                      fullWidth
                      autoFocus
                      value={values.code}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, '').slice(0, 8);
                        setFieldValue('code', next);
                      }}
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code && errors.code}
                      slotProps={{
                        htmlInput: {
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 8,
                          autoComplete: 'one-time-code',
                          autoCorrect: 'off',
                          autoCapitalize: 'off',
                          spellCheck: false,
                          'data-1p-ignore': true,
                          'data-lpignore': 'true',
                          'data-form-type': 'other',
                        },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.rememberDevice}
                          onChange={(e) => setFieldValue('rememberDevice', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t('login.rememberDevice')}
                    />
                  </>
                )}

                <Box>
                  {!needsTwoFactor && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
                      <Link component={RouterLink} to="/forgot-password" variant="body2">
                        {t('login.forgotPassword')}
                      </Link>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    intent="primary"
                    fullWidth
                    size="large"
                    disabled={isSubmitting || loading}
                    startIcon={
                      isSubmitting || loading ? <CircularProgress size={20} /> : undefined
                    }
                  >
                    {isSubmitting || loading
                      ? needsTwoFactor
                        ? t('login.twoFactorSubmitting')
                        : t('login.submitting')
                      : needsTwoFactor
                        ? t('login.twoFactorSubmit')
                        : t('login.submit')}
                  </Button>

                  {needsTwoFactor && (
                    <Button
                      type="button"
                      fullWidth
                      sx={{ mt: 2.5 }}
                      onClick={() => {
                        handleBackToLogin();
                        setFieldValue('code', '');
                        setFieldValue('password', '');
                      }}
                    >
                      {t('login.twoFactorBack')}
                    </Button>
                  )}
                </Box>
              </Stack>
            </Form>
          )}
        </Formik>
    </Box>
  );

  if (isAuthenticated) return null;
  return <AuthPageShell>{formCard}</AuthPageShell>;
};

export default Login;
